import { AddOutlined } from '@mui/icons-material';
import { alpha, Box, Button, Stack } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { PdfAgreementsTable } from './pdfAgreementsTable';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import DocumentPreview from 'components/v2/common/documentPreview';
import PageHeader from 'components/v2/common/pageHeader';
import OnboardingAgreementWrapper from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/MYCLIENTS/components/dashboard/salesOnboarding/views/AgreementModal/OnboardingAgreementWrapper';
import AgreementMilestonesModal from 'views/v2/Dashboards-B2B/components/backOfficeFlow/agreementMilestones/containers';

import { ACTIONS } from 'constant';
import {
    canOpenAgreementMilestones,
    isAgreementSigned,
    isAssessmentAgreement,
} from 'helpers/clientAgreements';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { RootState } from 'redux/store';
import type { ClientAgreementRow } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface PdfAgreementModalData {
    row?: ClientAgreementRow;
    file?: Blob;
    fileName?: string;
}

interface PdfAgreementDashboardProps {
    candidateId: string;
    rows: ClientAgreementRow[];
    loading: boolean;
    busyKey: string | null;
    reload: () => Promise<void>;
    download: (row: ClientAgreementRow, key: string) => Promise<void>;
    preview: (row: ClientAgreementRow, key: string) => Promise<Blob | null>;
    sendReminder: (row: ClientAgreementRow, key: string) => Promise<void>;
    removeAgreement: (agreementId: number | string) => Promise<void>;
}

const editorLocationState = (state: unknown): string | undefined => {
    if (!state || typeof state !== 'object' || !('from' in state)) return undefined;
    const from = (state as { from?: unknown }).from;
    return typeof from === 'string' ? from : undefined;
};

/**
 * Classic PDF agreement list. Template-retainer firms use the case flow instead.
 * Create and edit still open the existing onboarding agreement editor.
 */
export const PdfAgreementDashboard: React.FC<PdfAgreementDashboardProps> = ({
    candidateId,
    rows,
    loading,
    busyKey,
    reload,
    download,
    preview,
    sendReminder,
    removeAgreement,
}) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const activeCaseId = useSelector((state: RootState) => state.application.entity?.id);
    const { modalObject, setModalObject, onResetModalObj } = useModalObj<PdfAgreementModalData>();
    const editorOpenedAtRef = useRef(0);
    const skippedCaseRefresh = useRef(false);
    const openedEditPath = useRef<string | null>(null);
    const isEditRoute = location.pathname.endsWith('/edit');

    useEffect(() => {
        if (!skippedCaseRefresh.current) {
            skippedCaseRefresh.current = true;
            return;
        }
        void reload();
    }, [activeCaseId, reload]);

    useEffect(() => {
        if (!isEditRoute) {
            openedEditPath.current = null;
            return;
        }
        if (openedEditPath.current === location.pathname) return;
        openedEditPath.current = location.pathname;
        editorOpenedAtRef.current = Date.now();
        setModalObject((prev) => ({
            ...prev,
            action: ACTIONS.EDIT,
            data: prev.data,
            loading: false,
            disabled: false,
        }));
    }, [isEditRoute, location.pathname, setModalObject]);

    const closeEditor = () => {
        if (Date.now() - editorOpenedAtRef.current < 300) return;
        onResetModalObj();
        const from = editorLocationState(location.state);
        if (from) {
            navigate(from);
        } else if (isEditRoute) {
            navigate(location.pathname.replace(/\/[^/]+\/edit$/, '') || '..');
        }
        void reload();
    };

    const handleAction = (action: ACTIONS, row?: ClientAgreementRow, rowKey?: string) => {
        if (action === ACTIONS.DOWNLOAD) {
            if (!row || !rowKey || !isAgreementSigned(row)) return;
            void download(row, rowKey);
            return;
        }
        if (action === ACTIONS.RESEND) {
            if (!row || !rowKey) return;
            void sendReminder(row, rowKey);
            return;
        }
        if (action === ACTIONS.CREATE) {
            editorOpenedAtRef.current = Date.now();
            setModalObject((prev) => ({
                ...prev,
                action,
                data: null,
                loading: false,
                disabled: false,
            }));
            return;
        }
        if (action === ACTIONS.EDIT) {
            if (!row?.application_id) return;
            editorOpenedAtRef.current = Date.now();
            setModalObject((prev) => ({
                ...prev,
                action,
                data: { row },
                loading: false,
                disabled: false,
            }));
            navigate(`${row.application_id}/edit`, { state: { from: location.pathname } });
            return;
        }
        if (action === ACTIONS.DELETE) {
            if (!row || row.agreement_id == null) return;
            setModalObject((prev) => ({
                ...prev,
                action,
                data: { row },
                title: translate('deleteRetainerTitle'),
                content: translate('deleteRetainerContent'),
                loading: false,
                disabled: false,
            }));
            return;
        }
        if (action === ACTIONS.SIGN) {
            if (!row || !rowKey) return;
            if (!isAssessmentAgreement(row) && row.application_id == null) return;
            void openAgreementPreview(row, rowKey);
            return;
        }
        if (action === ACTIONS.VIEW && row && canOpenAgreementMilestones(row)) {
            setModalObject((prev) => ({
                ...prev,
                action,
                data: { row },
                loading: false,
                disabled: false,
            }));
        }
    };

    const openAgreementPreview = async (row: ClientAgreementRow, rowKey: string) => {
        const file = await preview(row, rowKey);
        if (!file) return;
        const safeName =
            (row.program_name || 'agreement').replace(/[^a-zA-Z0-9]+/g, '_') || 'agreement';
        const title = isAssessmentAgreement(row)
            ? translate('assessment')
            : row.program_name || translate('agreement');
        setModalObject((prev) => ({
            ...prev,
            action: ACTIONS.SIGN,
            data: { row, file, fileName: `${safeName}.pdf` },
            title,
            loading: false,
            disabled: false,
        }));
    };

    const handleConfirmDelete = async () => {
        const agreementId = modalObject.data?.row?.agreement_id;
        if (agreementId == null) return;
        setModalObject((prev) => ({ ...prev, loading: true, disabled: true }));
        try {
            await removeAgreement(agreementId);
            dispatch(setAlert({ message: translate('retainerDeleted'), type: 'success' }));
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, loading: false, disabled: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    const milestoneRow = modalObject.action === ACTIONS.VIEW ? modalObject.data?.row : undefined;
    const previewFile = modalObject.action === ACTIONS.SIGN ? modalObject.data?.file : undefined;
    const editorOpen = modalObject.action === ACTIONS.CREATE || modalObject.action === ACTIONS.EDIT;

    return (
        <>
            <Box sx={{ pb: 4 }}>
                <PageHeader title="agreements" description="noAgreementsYetDescription" />
                <PdfAgreementsTable
                    rows={rows}
                    loading={loading}
                    busyKey={busyKey}
                    handleAction={handleAction}
                />
                <Stack sx={{ alignItems: 'center', mt: 1 }}>
                    <Button
                        startIcon={<AddOutlined sx={{ width: 18, height: 18 }} />}
                        onClick={() => handleAction(ACTIONS.CREATE)}
                        sx={(theme) => ({
                            width: 'min(420px, 100%)',
                            minHeight: 38,
                            border: '1px dashed',
                            borderColor: alpha(theme.palette.primary.main, 0.5),
                            borderRadius: '14px',
                            bgcolor: alpha(theme.palette.primary.main, 0.06),
                            color: 'primary.dark',
                            fontWeight: 700,
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: alpha(theme.palette.primary.main, 0.72),
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                            },
                        })}
                    >
                        {translate('createAgreementRow')}
                    </Button>
                </Stack>
            </Box>

            {editorOpen ? <OnboardingAgreementWrapper open onClose={closeEditor} /> : null}

            {modalObject.action === ACTIONS.DELETE ? (
                <ConfirmationDialog
                    title={modalObject.title}
                    content={modalObject.content}
                    loading={modalObject.loading}
                    onClose={() => {
                        if (modalObject.loading) return;
                        onResetModalObj();
                    }}
                    onConfirm={() => void handleConfirmDelete()}
                />
            ) : null}

            {previewFile ? (
                <DocumentPreview
                    open
                    mobile={false}
                    onClose={onResetModalObj}
                    title={modalObject.title || translate('viewRetainer')}
                    fileName={modalObject.data?.fileName}
                    file={previewFile}
                />
            ) : null}

            <AgreementMilestonesModal
                open={milestoneRow != null}
                onClose={onResetModalObj}
                appId={
                    milestoneRow?.application_id != null
                        ? String(milestoneRow.application_id)
                        : null
                }
                agreementName={
                    milestoneRow
                        ? isAssessmentAgreement(milestoneRow)
                            ? translate('assessment')
                            : milestoneRow.program_name || ''
                        : undefined
                }
                candidateId={candidateId}
                canEditMilestones={milestoneRow ? !isAgreementSigned(milestoneRow) : false}
            />
        </>
    );
};
